/**
 * THE WORKSPACE'S LEFT RAIL — AI generation, audio editing, and versions.
 *
 * The six tools the drawer listed, plus Trim, Upload, Tempo & Pitch, Mix, Master and Version
 * History, but they behave like a tool palette rather than a menu: selecting one marks it as
 * active and opens its panel in the centre column, and the rail stays visible while you work.
 * In the drawer, opening a tool replaced the entire panel, so you lost sight of the
 * song and of the other tools.
 *
 * TWO GROUPS, NOT ONE LIST, and the split is the point rather than tidiness.
 *
 * AI GENERATION asks a model to produce audio. It costs credits, it takes time, it is
 * non-deterministic, and what comes back is new material — Extend, Remix, Replace, Split
 * Stems and Cover Art all work that way.
 *
 * AUDIO EDITING operates on a file that already exists. It is deterministic, it should cost
 * nothing, and it produces the same track altered — Trim, Tempo & Pitch, Mix and Master.
 *
 * Under one "AI tools" heading those were indistinguishable, which mattered in a specific
 * way: a user who wanted the song they had, slightly changed, would pick Remix and get a
 * different song, having been charged for it. Two headings make the difference visible
 * before the choice is made. Upload and Downloads sit with editing because neither is a
 * generation — one brings a file in and the other takes one out.
 *
 * ONE ROW IS STILL LISTED AND CANNOT RUN END TO END — Mix, which still waits on real stem
 * separation. Master now opens a real panel and, when the backend is configured, can measure
 * and master the current track.
 *
 * TRIM. Real end to end: a validated range over the song's waveform, sent to a real
 * dispatch. See src/api/audioEditApi.ts and components/studio/TrimPanel.
 *
 * TEMPO & PITCH. The arithmetic is real and unit tested (lib/tempoPitch.ts): a BPM change
 * gives a correct new duration, a semitone shift a correct frequency ratio. Applying either
 * needs a time-stretching DSP service. See components/studio/TempoPitchPanel.
 *
 * MIX. The mixer itself works — five faders, pans, exclusive solo, EQ and compressor — and
 * it opens as a real editing panel. Stem separation has to happen first for a given song,
 * but that is now a per-song prerequisite rather than a permanently missing backend. See
 * lib/stems.ts and components/studio/MixPanel.
 *
 * MASTER. The delivery targets are the platforms' own published loudness figures
 * (lib/mastering.ts). In backend mode the panel measures the current track and renders a
 * mastered version; without that backend it stays honestly marked as pending. See
 * components/studio/MasterPanel.
 *
 * UPLOAD. Real end to end: a file is chosen or dropped, checked for format and size,
 * identified by reading its first bytes, sent to `POST /uploads/audio`, independently
 * re-validated and decoded there, and added to the library as a new song. See
 * src/api/audioUploadApi.ts and components/studio/UploadPanel.
 *
 * Master carries "soon" only when its backend is absent. Mix stays in the rail as a real
 * editing panel; on a song with no stems yet, the panel says
 * exactly that and points back to Split Stems as the prerequisite.
 *
 * SIMULATED — THE GENERATION GROUP ONLY. Each of those five runs a 1300ms timer and appends
 * a version record; see src/hooks/useStudio.ts. Nothing in the editing group simulates
 * anything: no timer, no version, and no success message. That asymmetry is what the two
 * headings are for.
 *
 * MOBILE TOOL NAVIGATION.
 *
 * Below the workspace's 900px breakpoint, the inline rail stops being the primary
 * presentation. A horizontal scroller of eleven tool rows was an honest fallback when CSS
 * alone had to save the layout, but once JS can see the viewport there is a better shape:
 * one Tools trigger opening a grouped sheet. The tool state is the SAME state either way —
 * `active` and `onSelect` still come from Studio's one drawer field — and the list is the
 * same list. What changes is only how the list and the selected panel are presented on a
 * phone.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import {
  ChevronDown,
  Clock,
  Crop,
  Download,
  Gauge,
  History,
  Image as ImageIcon,
  Layers,
  ListTree,
  Scissors,
  SlidersHorizontal,
  Upload,
  Wand2,
  X,
} from 'lucide-react';
import { Tooltip } from '../ui';
import { audioEditApi } from '../../api/audioEditApi';
import { focusableWithin, wrapTabFocus } from '../../lib/focusTrap';
import { latestVersion, versionShortLabel } from '../../lib/versions';
import { c } from '../../styles/theme';
import type { Song, StudioDrawer } from '../../types/domain';

export interface ToolRailProps {
  song: Song;
  /** The open panel, so the rail can mark it active. */
  active: StudioDrawer;
  onSelect: (panel: StudioDrawer) => void;
  /** Whether the workspace is in its mobile tool-sheet mode. */
  mobile?: boolean;
  /** Whether the mobile tool sheet is open. */
  mobileSheetOpen?: boolean;
  /** Opens the mobile tool sheet onto its list view. */
  onOpenMobileSheet?: () => void;
  /** Closes the mobile tool sheet entirely. */
  onCloseMobileSheet?: () => void;
  /** Opens the Timeline inside the mobile tool sheet. */
  onOpenTimeline?: () => void;
  /** Selected mobile tool-sheet content, or null for the grouped list. */
  mobileSheetContent?: ReactNode | null;
  /** Current section name shown beside the Tools trigger. */
  mobileCurrentLabel?: string;
}

interface RailTool {
  panel: StudioDrawer;
  label: string;
  icon: ReactNode;
  /** The bubble's text for a row with no cost to explain. */
  hint?: string;
  /** Trailing text in place of a cost. */
  note?: string;
  /** Extra visible trailing content for a row that is otherwise available. */
  trailing?: ReactNode;
  /** Accessible name override, when the visible label is too terse. */
  fullLabel?: string;
}

/**
 * The generation tools, in the order the drawer listed them.
 *
 * Every row here costs credits and every one produces new audio from a model, which is
 * exactly what the group's heading claims — so a row with no `kind` does not belong in this
 * list at all, and none has one.
 */
const GENERATION_TOOLS: RailTool[] = [
  { panel: 'extend', label: 'Extend', icon: <Clock size={14} />, hint: 'Continue the song with newly generated audio.' },
  { panel: 'remix', label: 'Remix', icon: <Wand2 size={14} />, hint: 'Create a new interpretation of the song.' },
  { panel: 'replace', label: 'Replace', icon: <Scissors size={14} />, hint: 'Generate replacement audio for a selected section.' },
  { panel: 'stems', label: 'Split Stems', icon: <Layers size={14} />, hint: 'Separate the song into individual stems.' },
  { panel: 'cover', label: 'Cover Art', icon: <ImageIcon size={14} />, hint: 'Generate or upload artwork for this song.' },
];

/**
 * The editing tools. None costs anything, and none of them charges because none of them
 * generates — an edit to a file you already have is not a purchase.
 */
function editingTools(): RailTool[] {
  const canMaster = audioEditApi.supports('master');
  return [
    {
      panel: 'trim',
      label: 'Edit',
      icon: <Crop size={14} />,
      hint: 'Edit a selected section of the song — trim it, cut it, or prepare follow-up edits.',
    },
    {
      panel: 'tempo',
      label: 'Tempo & Pitch',
      icon: <Gauge size={14} />,
      hint: 'Work out and apply a tempo or key change.',
    },
    {
      panel: 'mix',
      label: 'Mix',
      icon: <SlidersHorizontal size={14} />,
      hint: 'Balance the individual tracks, shape EQ and dial in compression. Split stems first on songs that do not have them yet.',
    },
    {
      panel: 'master',
      label: 'Master',
      icon: <Gauge size={14} />,
      note: canMaster ? undefined : 'soon',
      hint: canMaster
        ? 'Choose a delivery loudness target and master the current track.'
        : 'Choose a delivery loudness target. Mastering is not available yet.',
    },
    {
      panel: 'upload',
      label: 'Upload',
      icon: <Upload size={14} />,
      hint: 'Bring your own audio file in and add it to your library.',
    },
    {
      panel: 'downloads',
      label: 'Downloads',
      icon: <Download size={14} />,
      hint: 'Download this song, its lyrics or its cover art.',
    },
  ];
}

export default function ToolRail({
  song,
  active,
  onSelect,
  mobile = false,
  mobileSheetOpen = false,
  onOpenMobileSheet,
  onCloseMobileSheet,
  onOpenTimeline,
  mobileSheetContent = null,
  mobileCurrentLabel,
}: ToolRailProps) {
  const latest = latestVersion(song);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = useId();
  const editing = editingTools();

  /*
    Compare is the Versions tool's second surface. Studio.tsx already maps it back to
    `versions` before the prop reaches here; doing the same read defensively keeps this
    component's own "current tool" label truthful if that caller ever changes.
  */
  const currentPanel = active === 'compare' ? 'versions' : active;

  const versionTool: RailTool = {
    panel: 'versions',
    label: `History (${song.versions.length})`,
    icon: <History size={14} />,
    trailing: latest ? (
      <span style={{ fontSize: 10, color: c.text2, flexShrink: 0 }}>{versionShortLabel(latest)}</span>
    ) : null,
    fullLabel: `Version History (${song.versions.length})`,
    hint: 'Every version of this song. Compare, load or rename them.',
  };
  const timelineTool: RailTool = {
    panel: null,
    label: 'Timeline',
    icon: <ListTree size={14} />,
    hint: 'Song details, prompt and timeline sections.',
  };

  const allTools = [timelineTool, ...GENERATION_TOOLS, ...editing, versionTool];
  const currentLabel =
    mobileCurrentLabel ??
    allTools.find((tool) => tool.panel === currentPanel)?.fullLabel ??
    allTools.find((tool) => tool.panel === currentPanel)?.label ??
    'Timeline';

  /*
    Focus into the sheet and back out when it closes, the same mechanics the nav drawer and
    the Vault's drawer use. The trigger is the natural return target here because selecting a
    tool changes the workspace under the sheet rather than navigating away from it.
  */
  useEffect(() => {
    if (!mobileSheetOpen) return;
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = menuRef.current;
    if (root) focusableWithin(root)[0]?.focus();
    return () => {
      const target = triggerRef.current?.isConnected ? triggerRef.current : previouslyFocused.current;
      previouslyFocused.current = null;
      if (target?.isConnected) target.focus();
    };
  }, [mobileSheetOpen]);

  // The page behind does not keep scrolling while a sheet is over it.
  useEffect(() => {
    if (!mobileSheetOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileSheetOpen]);

  // Escape closes, and Tab stays inside while the tool sheet is open.
  useEffect(() => {
    if (!mobileSheetOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const root = menuRef.current;
      if (!root) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseMobileSheet?.();
        return;
      }
      if (e.key !== 'Tab') return;
      wrapTabFocus(root, e);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [mobileSheetOpen, onCloseMobileSheet]);

  function select(tool: RailTool) {
    if (mobile && tool.panel === null) {
      onOpenTimeline?.();
      return;
    }
    if (tool.panel !== null) onSelect(tool.panel);
  }

  /** One rail row. Active rows carry the accent; the rest are quiet. */
  function row(tool: RailTool) {
    const on = tool.panel === null ? currentLabel === 'Timeline' : currentPanel === tool.panel;
    const fullLabel = tool.fullLabel
      ? tool.fullLabel
      : tool.note
        ? `${tool.label} — not available yet`
        : tool.label;
    const hint = tool.hint ?? tool.label;

    return (
      <li key={tool.panel ?? 'timeline'} style={{ listStyle: 'none' }}>
        <Tooltip label={hint} placement="bottom" describe className="lyx-ws-tool-wrap">
          <button
            type="button"
            onClick={() => select(tool)}
            aria-pressed={on}
            aria-label={fullLabel}
            className="lyx-ws-tool"
            style={{
              border: `1px solid ${on ? c.accentBorder : 'transparent'}`,
              background: on ? c.accentSoft : 'transparent',
              color: on ? c.text1 : c.text2,
            }}
          >
            <span style={{ display: 'flex', color: on ? c.accent : c.text2, flexShrink: 0 }}>{tool.icon}</span>
            <span style={{ flex: 1, minWidth: 0 }}>{tool.label}</span>
            {tool.note ? (
              <span className="lyx-ws-tool-note" style={{ fontSize: 10, color: c.text2, flexShrink: 0 }}>
                {tool.note}
              </span>
            ) : (
              tool.trailing
            )}
          </button>
        </Tooltip>
      </li>
    );
  }

  /** One group of rows, under its own heading and in its own list. */
  function group(heading: string, listLabel: string, tools: RailTool[], marginTop?: number) {
    return (
      <div className="lyx-ws-tool-sheet-group">
        <p className="lyx-ws-eyebrow" style={marginTop ? { marginTop } : undefined}>
          {heading}
        </p>
        <ul className="lyx-ws-tool-list" aria-label={listLabel}>
          {tools.map((tool) => row(tool))}
        </ul>
      </div>
    );
  }

  function inlineRail() {
    return (
      <section className="lyx-ws-rail lyx-ws-rail-inline" aria-label="Studio tools and versions">
        {group('AI generation', 'AI generation tools', GENERATION_TOOLS)}
        {group('Audio editing', 'Audio editing tools', editing, 18)}
        {group('Versions', 'Versions', [versionTool], 18)}
      </section>
    );
  }

  function mobileRail() {
    return (
      <section className="lyx-ws-rail lyx-ws-rail-mobile" aria-label="Studio tools and versions">
        <button
          ref={triggerRef}
          type="button"
          className="lyx-ws-tool-sheet-trigger"
          onClick={onOpenMobileSheet}
          aria-label={`Open tools menu — current ${currentLabel}`}
          aria-haspopup="dialog"
          aria-expanded={mobileSheetOpen}
          aria-hidden={mobileSheetOpen || undefined}
          tabIndex={mobileSheetOpen ? -1 : undefined}
        >
          <span className="lyx-ws-tool-sheet-label">
            <SlidersHorizontal size={15} color={c.text2} />
            Tools
          </span>
          <span className="lyx-ws-tool-sheet-current">{currentLabel}</span>
          <ChevronDown size={14} color={c.text2} />
        </button>

        {mobileSheetOpen && (
          <>
            {/* Decorative: the real dismissal is the Close button inside the sheet. */}
            <div className="lyx-drawer-overlay" onClick={onCloseMobileSheet} aria-hidden="true" />
            <div
              ref={menuRef}
              className="lyx-drawer lyx-ws-tool-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby={headingId}
              tabIndex={-1}
            >
              <p id={headingId} className="lyx-visually-hidden">
                Studio tools
              </p>
              {mobileSheetContent ? (
                <div className="lyx-ws-tool-sheet-panel">{mobileSheetContent}</div>
              ) : (
                <div className="lyx-ws-tool-sheet-body">
                  <div className="lyx-ws-tool-sheet-head">
                    <div style={{ minWidth: 0 }}>
                      <p className="lyx-ws-tool-sheet-title">Studio tools</p>
                      <p className="lyx-ws-tool-sheet-sub">Current: {currentLabel}</p>
                    </div>
                    <button
                      type="button"
                      className="lyx-ws-tool-sheet-close"
                      aria-label="Close tools menu"
                      onClick={onCloseMobileSheet}
                    >
                      <X size={16} color={c.text2} />
                    </button>
                  </div>

                  {group('Workspace', 'Workspace sections', [timelineTool])}
                  {group('AI generation', 'AI generation tools', GENERATION_TOOLS, 18)}
                  {group('Audio editing', 'Audio editing tools', editing, 18)}
                  {group('Versions', 'Versions', [versionTool], 18)}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    );
  }

  return mobile ? mobileRail() : inlineRail();
}
